import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { VehicleModels } from '@models/vehicle.model';
import { VehiclesActions } from '@states/vehicles/vehicles.action-types';
import { UiButtonComponent } from '@ui';
import VehicleColors = VehicleModels.VehicleColors;
import VehicleColor = VehicleModels.VehicleColor;

interface DialogData {
  vehicle: VehicleModels.Vehicle;
}

@Component({
  selector: 'app-vehicle-edit-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatSelectModule,
    TranslateModule,
    UiButtonComponent,
  ],
  templateUrl: './vehicle-edit-modal.component.html',
  styleUrl: './vehicle-edit-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VehicleEditModalComponent implements OnInit {
  private readonly dialogRef = inject(
    MatDialogRef<VehicleEditModalComponent>,
  );
  protected readonly data = inject<DialogData>(MAT_DIALOG_DATA);
  private readonly store$ = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translateService = inject(TranslateService);

  protected readonly editableVehicle = signal<VehicleModels.Vehicle>(
    structuredClone(this.data.vehicle),
  );

  protected readonly vehicleColorOptions = Object.values(VehicleColors);
  protected readonly colorControl = new FormControl<VehicleColor | null>(null);

  public ngOnInit(): void {
    // Initialize color control
    const initialColor = this.findColorByValue(this.data.vehicle.color);
    this.colorControl.setValue(initialColor);

    // Listen to color changes
    this.colorControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((colorInfo) => {
        if (colorInfo) {
          const vehicle = this.editableVehicle();
          this.editableVehicle.set({ ...vehicle, color: colorInfo.name });
        }
      });
  }

  private findColorByValue(
    colorValue: string | null | undefined,
  ): VehicleColor | null {
    if (!colorValue) return null;
    return (
      VehicleColors.find(
        (c) => c.value.toLowerCase() === colorValue.toLowerCase(),
      ) || null
    );
  }

  public onSave(): void {
    const vehicle = {
      ...this.editableVehicle(),
      color: this.colorControl.value?.value || this.editableVehicle().color,
    };

    this.store$.dispatch(VehiclesActions.editVehicle({ vehicle }));
    this.close();
  }

  public onCancel(): void {
    this.close();
  }

  public close(): void {
    this.dialogRef.close();
  }
}
